import React, { useEffect, useMemo, useState } from 'react';
import { Transcript } from '~/types/models';
import cx from 'classnames';
import { CopyToClipboardButton } from './CopyToClipboardButton';
import { msToMinuteSeconds } from '~/utils/date';
import Popover, { Alignment } from '~/App/Components/Popover';

export interface TranscriptReaderPanelProps {
  transcript: Transcript;
  className?: string;
  sentenceClassName?: string;
  hideCopyButton?: boolean;
}

export const TranscriptReaderPanel = ({
  transcript,
  className,
  sentenceClassName,
  hideCopyButton,
}: TranscriptReaderPanelProps) => {
  const [isLinkCopied, setIsLinkCopied] = useState(false);

  const clipboardText = useMemo(() => {
    const textArray = transcript?.captions?.sentences?.map(
      (sentence) => sentence.text
    );

    return textArray?.join('\r\n\r\n');
  }, [transcript]);

  useEffect(() => {
    if (!isLinkCopied) return;

    const timer = setTimeout(() => {
      setIsLinkCopied(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, [isLinkCopied]);

  return (
    <div
      className={cx(className, [
        'w-full h-full relative v2-bg-neutral-50',
        'border border-solid v2-border-neutral-300 overflow-y-auto',
        'flex flex-col gap-y-2 p-[15px]',
      ])}
    >
      {!hideCopyButton && (
        <Popover
          align={Alignment.Middle}
          place='left'
          tipSize={0.1}
          isOpen={isLinkCopied}
          onOuterAction={() => setIsLinkCopied(false)}
          body={
            <div className='rounded-md v2-bg-black text-white text-sm font-bold p-3'>
              Transcript copied!
            </div>
          }
        >
          <CopyToClipboardButton
            className='top-3 right-3'
            clipboardText={clipboardText}
            onSuccess={() => setIsLinkCopied(true)}
            active={isLinkCopied}
          />
        </Popover>
      )}
      {transcript.captions?.sentences?.map((sentence, index) => {
        return (
          <div
            key={index}
            className={cx(
              sentenceClassName,
              'flex flex-row gap-x-3 v2-text-neutral-500 text-base'
            )}
          >
            <span>{msToMinuteSeconds(sentence?.start)}</span>
            <span>{sentence?.text}</span>
          </div>
        );
      })}
    </div>
  );
};
